import classNames from "classnames";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";
import { ReactComponent as RestartIcon } from "assets/images/common/restart.svg";

import useDebounce from "hooks/useDebounce";

import Button from "components/Button";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";
import TableRowActions from "components/Table/TableRowActions";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  addCompanyUserRequest,
  changeCompanyUsersPage,
  getCompanyUsersRequest,
  resendCompanyUserInvitationRequest,
  resetSuccessfullyAddedCompany,
  resetSuccessfullyRemovedCompany,
  resetSuccessfullyUpdatedCompany,
  updateCompanyUserRequest,
} from "store/slices/companiesSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import { ModalType } from "entities/common";
import PageTemplate from "modules/common/PageTemplate";

import { AddUserRequest, UserFormValues, UserStatuses } from "entities/companies";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import styles from "./style.module.scss";

const CompanyUsersTab = () => {
  const { id: companyId } = useParams();
  const callbackKey = useId();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const { companyUsers, isCompanyUsersLoading, successfullyAddedId, successfullyUpdatedId, successfullyRemovedId } =
    useAppSelector((state: RootStore) => state.companies);
  const usersSuccessfullyRemovedId = useAppSelector((state: RootStore) => state.users.successfullyRemovedId);
  const [status, setStatus] = useState<UserStatuses | "all">("all");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSaveUser = useCallback(
    (values?: unknown) => {
      const data = values as UserFormValues;

      const body: AddUserRequest = {
        full_name: data.name,
        email: data.email,
        phone: data.phone,
        position: data.position,
      };

      dispatch(addCompanyUserRequest({ companyId: Number(companyId), body }));
    },
    [companyId, dispatch],
  );

  const handleUpdateUser = useCallback(
    (id: number) => (values?: unknown) => {
      const data = values as UserFormValues;

      const body: AddUserRequest = {
        full_name: data.name,
        email: data.email,
        phone: data.phone,
        position: data.position,
      };

      dispatch(updateCompanyUserRequest({ companyId: Number(companyId), userId: id, body }));
    },
    [companyId, dispatch],
  );

  const handleCreate = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSaveUser });

    dispatch(
      showModal({
        modalType: ModalType.USER,
        modalProps: { type: "add", callbackKey },
      }),
    );
  }, [callbackKey, dispatch, handleSaveUser]);

  const handleEdit = useCallback(
    (userData: {
      id: number;
      name: string;
      email: string;
      phone: string;
      status: UserStatuses;
      position: string | null;
      isOwner: boolean;
    }) =>
      (id: number) => {
        setModalCallbacks(callbackKey, { onSave: handleUpdateUser(id) });

        dispatch(
          showModal({
            modalType: ModalType.USER,
            modalProps: {
              type: "edit",
              callbackKey,
              data: {
                name: userData.name,
                email: userData.email,
                phone: userData.phone,
                position: userData.position,
              },
            },
          }),
        );
      },
    [callbackKey, dispatch, handleUpdateUser],
  );

  const handleRemove = useCallback(
    (portfolioCounts: number) => (id: number) => {
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_USER,
          modalProps: { id, companyId: Number(companyId), portfolioCounts },
        }),
      );
    },
    [companyId, dispatch],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(changeCompanyUsersPage(page));
    },
    [dispatch],
  );

  // TODO add logic
  const handleResend = useCallback(
    (id: number) => {
      dispatch(resendCompanyUserInvitationRequest({ companyId: Number(companyId), userId: id }));
    },
    [companyId, dispatch],
  );

  const handleStatusChange = useCallback((value: string) => {
    setStatus(value as UserStatuses | "all");
  }, []);

  const statusOptions = useMemo(
    () => [
      { label: "All Statuses", value: "all" },
      { label: "Active", value: UserStatuses.ACTIVE },
      { label: "Pending", value: UserStatuses.PENDING },
      { label: "Invitation Expired", value: UserStatuses.INVITATION_EXPIRED },
    ],
    [],
  );

  const tableHeaders = useMemo(
    () => [
      { label: "User", key: "user" },
      { label: "Contact", key: "contact" },
      { label: "Status", key: "status" },
      { label: "Actions", key: "actions" },
    ],
    [],
  );

  const users = useMemo(
    () =>
      companyUsers.list.map((user) => ({
        id: user?.id,
        name: user?.full_name || "-",
        email: user?.email || "-",
        phone: user?.phone || "-",
        status: user?.status,
        position: user?.position,
        isOwner: user?.is_owner,
        portfolioCounts: user?.managed_portfolios_count,
      })),
    [companyUsers.list],
  );

  const tableData = useMemo(
    () =>
      users.map((row) => {
        return {
          id: row.id,
          user: (
            <div className={styles.user}>
              <span className={styles.userName}>{row.name}</span>
              <span className={styles.userPosition}>
                {row.position}
                {row.isOwner && row.position ? " / " : ""}
                {row.isOwner ? <span className={styles.userOwner}>Owner</span> : ""}
              </span>
            </div>
          ),
          contact: (
            <div className={styles.contact}>
              <span className={styles.contactEmail}>{row.email}</span>
              <span className={styles.contactPhone}>{row.phone}</span>
            </div>
          ),
          status: (
            <span className={classNames(styles["status-block"])}>
              <span className={classNames(styles.status, styles[row.status])}>
                {row.status === UserStatuses.ACTIVE
                  ? "Active"
                  : row.status === UserStatuses.PENDING
                    ? "Pending"
                    : row.status === UserStatuses.INVITATION_EXPIRED
                      ? "Invitation expired"
                      : ""}
              </span>

              {row.status === UserStatuses.INVITATION_EXPIRED && (
                <Button variant="link" className={styles["status-button"]} onClick={() => handleResend(row.id)}>
                  <RestartIcon />
                </Button>
              )}
            </span>
          ),
          actions: (
            <TableRowActions
              id={row.id}
              flags={{ view: false, edit: true, remove: true }}
              onEdit={handleEdit(row)}
              onRemove={handleRemove(row.portfolioCounts)}
              isDisabledRemove={row.isOwner}
              disabledRemoveMessage={row.isOwner ? "The owner cannot be deleted" : undefined}
            />
          ),
        };
      }),
    [handleEdit, handleRemove, handleResend, users],
  );

  // get company users
  useEffect(() => {
    dispatch(
      getCompanyUsersRequest({
        companyId: Number(companyId),
        query: QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(),
          [QUERY_PARAMS.PAGE]: companyUsers.meta.page,
          [QUERY_PARAMS.STATUS]: status,
        }),
      }),
    );
  }, [
    dispatch,
    status,
    companyId,
    debouncedSearch,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
    usersSuccessfullyRemovedId,
    companyUsers.meta.page,
  ]);

  // successfully added user
  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(resetSuccessfullyAddedCompany());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-added-user",
          message: "The user was successfully added.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyAddedId, dispatch]);

  // successfully updated user
  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(resetSuccessfullyUpdatedCompany());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-updated-user",
          message: "The user was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  // successfully removed user
  useEffect(() => {
    if (successfullyRemovedId) {
      dispatch(resetSuccessfullyRemovedCompany());
      dispatch(
        showToast({
          id: "successfully-removed-user",
          message: "The user was successfully removed.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyRemovedId, dispatch]);

  return (
    <PageTemplate
      title="Company users"
      description="Manage user access"
      actions={
        <Button variant="primary" className={styles["header-create"]} onClick={handleCreate}>
          <span className={styles["header-create-text"]}>Invite user</span>
          <span className={styles["header-create-icon"]}>
            <PlusIcon />
          </span>
        </Button>
      }
      wrapperClassName={styles.page}
    >
      <>
        <div className={styles["action-container"]}>
          <SearchInput
            name="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search users..."
            className={styles["search-input"]}
          />

          <Select
            options={statusOptions}
            onChange={handleStatusChange}
            value={status}
            className={styles["status-select"]}
          />
        </div>

        {tableData?.length === 0 && (
          <div className={styles["not-found"]}>
            <NotFoundIcon />

            <p>{tableData?.length === 0 && !debouncedSearch ? "No users yet" : "No users found"}</p>
          </div>
        )}

        {!!tableData && tableData.length > 0 && (
          <div className={styles["table-scroll"]}>
            <Table
              headers={tableHeaders}
              data={tableData}
              isLoading={isCompanyUsersLoading}
              pagination={{
                total: companyUsers.meta.total_pages,
                page: companyUsers.meta.page,
                onPageChange: handlePageChange,
              }}
              className={styles.table}
            />
          </div>
        )}
      </>
    </PageTemplate>
  );
};

export default CompanyUsersTab;
